import type { BaseTableResultItem, TableResult } from "@tanstack/react-table";

import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES } from "@lib/shared_constants";

export interface CreativeVariantResultItem extends BaseTableResultItem {
  id: string;
  creative: string;
  compatible_base_creative_campaign_ids: string[];
}

/** The raw shape Rails returns for a single creative variant before DataTable normalization. */
export interface RawCreativeVariantItem {
  variant_id: number;
  creative: string;
  compatible_base_creative_campaign_ids: string[];
}

export interface RawCreativeVariantsResponse {
  hits: number;
  results: RawCreativeVariantItem[];
}

export interface DecisioningStudioAgentsAppGroupIdDsaIdCreativeComponentsComponentVariantsQuery {
  search_term?: string;
}

export interface DecisioningStudioAgentsAppGroupIdDsaIdCreativeComponentsComponentVariantsParams {
  /** the unique identifier for the current app group */
  appGroupId: string;
  /** the unique identifier for the decisioning studio agent */
  dsaId: string;
  /** the creative-component slug (e.g. "subject_line", "body_header") */
  component: string;
  /** the number of records to return per page */
  limit?: number;
  /** the index for the first record in the set among the entire response */
  start?: number;
  /** optional filters including search_term */
  query?: DecisioningStudioAgentsAppGroupIdDsaIdCreativeComponentsComponentVariantsQuery;
}

export type GetDataTableDecisioningStudioAgentsAppGroupIdDsaIdCreativeComponentsComponentVariantsResult =
  TableResult<CreativeVariantResultItem>;

// Union over the warning `type` slugs the backend emits. Narrowed (rather than
// `string`) so `useShowVariantWarnings` can switch exhaustively — adding a new
// backend warning type will fail the FE compile until a matching case is added.
export type CreativeVariantWarningType = "orphaned_variant";

export interface CreativeVariantWarning {
  type: CreativeVariantWarningType;
  message: string;
}

export interface CreativeVariantAction {
  variant_id: number;
  creative: string;
  compatible_base_creative_campaign_ids: string[];
}

export interface PostDecisioningStudioAgentsAppGroupIdDsaIdCreativeComponentsComponentVariantsProps {
  appGroupId: string;
  dsaId: string;
  component: string;
  creative: string;
  base_creative_campaign_ids: string[];
}

export interface PostDecisioningStudioAgentsAppGroupIdDsaIdCreativeComponentsComponentVariantsResult {
  variant: CreativeVariantAction;
  warnings: CreativeVariantWarning[];
}

/** Rejection for a creative that already belongs to an active variant in the same component. */
export type CreativeVariantDuplicateError = FetchUserEncodedError<
  SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["DUPLICATE_VARIANT"]
>;

// Keyed off `error_code`, not the HTTP status, so the other 422s keep the generic form copy.
// The POST and PUT variant endpoints return the same body, so one guard serves both.
export const isCreativeVariantDuplicateError = (
  error: FetchRequestError
): error is CreativeVariantDuplicateError => {
  return error instanceof FetchUserEncodedError && error.errorCode === "DUPLICATE_VARIANT";
};
